import { EquipmentLocationEventType } from '__generated__/graphql';
import { IGetEquipmentLocationQuery } from 'containers/project-planning/hooks/useGetEquipmentLocation/useGetEquipmentLocation';
import { t } from 'i18next';
import { isNil, sortBy } from 'lodash';
import { Nullable } from 'types/util/Nullable';
import { dateTimeFromUtcISO } from 'utils/dateUtils';
import { distanceMeters } from 'utils/locationUtils';

export enum IntegratedEquipmentLocationSource {
  ALL = 'all',
  TRACKING_DEVICE = 'tracking_device',
  CLOCK_ACTIONS = 'clock_actions',
}

export enum IntegratedEquipmentLocationRangeType {
  RECENT_LOCATIONS = 'recent_locations',
  DAY_RANGE = 'day_range',
}

export enum IntegratedEquipmentApiSource {
  FLEET_WATCHER = 'fleet_watcher',
  BUSYBUSY = 'busybusy',
}

export interface IIntegratedEquipmentLocation {
  id: string;
  equipmentId: string;
  latitude?: Nullable<string>;
  longitude?: Nullable<string>;
  altitude?: Nullable<string>;
  utc: string; // "yyyy-MM-dd'T'HH:mm:ss" (no Z)
  horizontalAccuracy?: Nullable<string>;
  verticalAccuracy?: Nullable<string>;
  eventType?: Nullable<EquipmentLocationEventType>;
  deviceType?: Nullable<string>;
  source?: IntegratedEquipmentApiSource;
  locationCount: number;
  locationGroupEndUtcTime?: Nullable<string>;
}

export interface IIntegratedEquipmentLocationResponse {
  success: boolean;
  data: IIntegratedEquipmentLocation[];
}

export function filteredDeduped(allLocations: IIntegratedEquipmentLocation[]): IIntegratedEquipmentLocation[] {
  const seenLocations = new Set<string>();
  const locations: IIntegratedEquipmentLocation[] = [];

  for (const location of allLocations) {
    if (!location?.utc || !location?.equipmentId) {
      continue;
    }

    if (location.source !== IntegratedEquipmentApiSource.FLEET_WATCHER) {
      continue;
    }

    const key = `${location.equipmentId}|${location.utc}`;
    if (seenLocations.has(key)) {
      continue;
    }

    seenLocations.add(key);
    locations.push(location);
  }

  return locations;
}

export function groupLocationsByDistance(
  locations: IIntegratedEquipmentLocation[],
  maxMeters = 20
): IIntegratedEquipmentLocation[] {
  if (!locations.length) return [];

  const sorted = sortBy(locations, 'utc');

  const result: IIntegratedEquipmentLocation[] = [];
  let anchor = sorted[0];

  result.push({
    ...anchor,
    locationCount: 1,
    locationGroupEndUtcTime: undefined,
  });

  for (const item of sorted.slice(1)) {
    const lastOut = result[result.length - 1];

    if (isNil(anchor.latitude) || isNil(anchor.longitude) || isNil(item.latitude) || isNil(item.longitude)) {
      continue;
    }

    const d = distanceMeters(
      Number(anchor.latitude),
      Number(anchor.longitude),
      Number(item.latitude),
      Number(item.longitude)
    );

    if (d > maxMeters) {
      anchor = item;
      result.push({
        ...item,
        locationCount: 1,
        locationGroupEndUtcTime: undefined,
      });
    } else {
      result[result.length - 1] = {
        ...lastOut,
        locationCount: (lastOut.locationCount ?? 1) + 1,
        locationGroupEndUtcTime: item.utc,
      };
    }
  }

  return result;
}

export function getEventTitle(
  eventType?: Nullable<EquipmentLocationEventType>,
  source?: Nullable<IntegratedEquipmentApiSource>
): string {
  switch (eventType) {
    case EquipmentLocationEventType.ClockIn:
      return t('Clock In');
    case EquipmentLocationEventType.ClockOut:
      return t('Clock Out');
    case EquipmentLocationEventType.BreakStart:
      return t('Break Start');
    case EquipmentLocationEventType.BreakEnd:
      return t('Break End');
    default:
      return source === IntegratedEquipmentApiSource.BUSYBUSY ? t('Clock Action') : t('GPS Tracker');
  }
}

export function getGroupDateString(location: IIntegratedEquipmentLocation, format: string = 'MMM d t'): string {
  const date = dateTimeFromUtcISO(location.utc).toLocal();
  if (!location.locationGroupEndUtcTime) {
    return date.toFormat('EEE, DD @ t');
  }

  const start = date.toFormat(format);
  const end = dateTimeFromUtcISO(location.locationGroupEndUtcTime).toLocal().toFormat(format);

  return start + ' - ' + end;
}

export function getAccuracyString(location: IIntegratedEquipmentLocation): string {
  if (location.horizontalAccuracy && Number(location.horizontalAccuracy) > 0) {
    return Number(location.horizontalAccuracy).toFixed(2) + ' ' + t('mi acc.');
  }
  return 'n/a acc.';
}

export function convertEquipmentLocationToIntegrated(
  location: IGetEquipmentLocationQuery
): IIntegratedEquipmentLocation {
  return {
    id: location.id,
    equipmentId: location.equipmentId,
    latitude: location.latitude?.toString(),
    longitude: location.longitude?.toString(),
    utc: location.utc,
    horizontalAccuracy: location.horizontalAccuracy?.toString(),
    verticalAccuracy: location.verticalAccuracy?.toString(),
    eventType: location.eventType,
    source: IntegratedEquipmentApiSource.BUSYBUSY,
    locationCount: 1,
  };
}

export function newestEquipmentLocation(
  a?: IIntegratedEquipmentLocation | null,
  b?: IIntegratedEquipmentLocation | null
): IIntegratedEquipmentLocation | null {
  if (!a && !b) {
    return null;
  }
  if (a && !b) {
    return a;
  }
  if (!a && b) {
    return b;
  }

  const aUtc = dateTimeFromUtcISO(a!.utc);
  const bUtc = dateTimeFromUtcISO(b!.utc);

  return bUtc! > aUtc! ? b! : a!;
}
